import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const IDLE_MS = 30 * 60 * 1000; // 30 minutes
const WARN_MS = 60 * 1000;      // warn 60s before logout

const ACTIVITY_EVENTS = ["mousemove", "keydown", "click", "scroll", "touchstart", "visibilitychange"];

/**
 * IdleWatcher — HIPAA-oriented auto-logout after 30 minutes of no user
 * activity. Silently mounted at the top of the tree; only kicks in when
 * a user is signed in.
 */
export default function IdleWatcher() {
  const { user, logout } = useAuth();
  const timerRef = useRef(null);
  const warnRef = useRef(null);

  useEffect(() => {
    // Only guard signed-in sessions.
    if (!user || user === false) return undefined;

    const clear = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (warnRef.current) clearTimeout(warnRef.current);
    };

    const arm = () => {
      clear();
      warnRef.current = setTimeout(() => {
        toast.info("You'll be signed out in 60 seconds due to inactivity. Move the mouse to stay signed in.", {
          duration: 8000,
        });
      }, IDLE_MS - WARN_MS);
      timerRef.current = setTimeout(async () => {
        toast.warning("Signed you out — 30 minutes of inactivity.");
        try {
          await logout();
        } catch {}
        window.location.href = "/login";
      }, IDLE_MS);
    };

    const onActivity = () => {
      if (document.visibilityState === "hidden") return; // ignore hidden-tab wakes
      arm();
    };

    arm();
    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }));

    return () => {
      clear();
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, onActivity));
    };
  }, [user, logout]);

  return null;
}
